import React from 'react'
import T from 'prop-types'
import {observer, PropTypes as mobxTypes} from 'mobx-react'
import rafSchedule from 'raf-schd'
import ResizeObserver, { DOMRect } from 'react-resize-observer'
import { 
  InfiniteScroll,
  CircleLoader
} from "react-base"
import { Rows } from './Rows'
import { Cells } from './Cells'
import { RowHeader } from './RowHeader'
import SchemerModel from '../../core/SchemerModel'
// import { ConstructableSchemerModel } from '../../core/decorators/constants'
import {TableStore} from './TableStore'

const USE_REQUEST_IDLE = false

export interface TableProps {
  data: Map<string, SchemerModel>
  // model: ConstructableSchemerModel
  isLoading: boolean
  isLoadingMore: boolean
  onRequestNextPage: () => void
  isReadyForNextPage: boolean,
  totalCount: number
  sortField?: string
  sortDir?: 'asc'|'desc'
  onChangeSort?: (newSortField: string, newSortDir: 'asc'|'desc') => void,
}

interface TablePropsInternal extends TableProps {
  tableStore: TableStore
}

@observer
export class Table extends React.Component<TablePropsInternal> {
  static DisplayName = "Schemer.Table"

  static propTypes = {
    data: mobxTypes.observableMap.isRequired,
    isLoading: T.bool,
    isLoadingMore: T.bool,
    onRequestNextPage: T.func.isRequired
  }

  private idleCallbackHandle?: number

  handleScroll = (scrollNode: Element) => {
    const {
      scrollTop,
      scrollLeft
    } = scrollNode
    this.cleanupCallbacks()

    if (USE_REQUEST_IDLE) {
      this.idleCallbackHandle = window.requestIdleCallback(() => {
        if (this.props.tableStore) {
          this.props.tableStore.setScroll(scrollTop, scrollLeft)
        }
      })  
    }
    else {
      if (this.props.tableStore) {
        this.props.tableStore.setScroll(scrollTop, scrollLeft)
      }
    }
  }

  handleScrollThrottled = rafSchedule(this.handleScroll)

  handleResize = (contentRect?: DOMRect) => {
    if (contentRect && this.props.tableStore) {
      this.props.tableStore.setDims(contentRect.height, contentRect.width)
    }
  }

  private cleanupCallbacks () {
    if (this.idleCallbackHandle && this.idleCallbackHandle > 0) {
      window.cancelIdleCallback(this.idleCallbackHandle)
    }
  }

  componentWillUnmount() {
    this.cleanupCallbacks()
  }

  renderContent = () => {
    if (!this.props.tableStore) {
      return null
    }    

    const cells = this.props.tableStore.visibleCells

    return cells && cells.visibleRowIds.length > 0 && this.props.tableStore.tableMetrics ? (
      <div 
        className={`sch_table_scroll_content`}
        style={{
          height: this.props.tableStore.tableMetrics.totalHeight,
          width: this.props.tableStore.tableMetrics.totalWidth
        }}
      >
        <header
          className={`sch_header_row ${cells.stuckTopRows.includes('_header_row') ? 'is_stuck' : ''}`}
          style={{
            height: this.props.tableStore.tableMetrics.headerHeight
          }}
        >
          {this.props.tableStore.enableRowHeader &&
            <RowHeader
              isStuck={cells.stuckLeftCols.includes('_header_col')}
              isSticky
              isOriginHeader
              width={this.props.tableStore.tableMetrics.rowHeaderWidth}
            />
          }
          <Cells  
            isHeaderRow
            rowId={`_header_row`}
            sortField={this.props.sortField}
            sortDir={this.props.sortDir}
            onChangeSort={this.props.onChangeSort}
            row={this.props.data.values().next().value} // First row
          />
        </header>
        <Rows />
      </div>
    ) : null
  }

  render () {
    const {
      data,
      isLoading,
      onRequestNextPage,
      isReadyForNextPage,
      totalCount
    } = this.props

    return (
      <div className={`sch_table`}>
        <InfiniteScroll 
          className={`sch_table_scrollbars`}
          onScroll={this.handleScroll}
          isReadyForNextEvent={isReadyForNextPage}
          endOfList={data && data.size === totalCount}
          onThresholdMet={onRequestNextPage}
          outside
        >{this.renderContent()}</InfiniteScroll>
        {data.size === 0 && !isLoading &&
          <div className={`sch_table_status no_results`}>No results</div>
        }
        <CircleLoader loading={isLoading} overlay />
        <ResizeObserver
          onResize={this.handleResize}
        />
      </div>
    )
  }
}